import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../lib/api.js";
import { APP_DOWNLOAD_URL, APP_DOWNLOAD_NAME, APP_PLATFORM_LABEL } from "../lib/app.js";
import { RECONNECT_TOKEN_KEY } from "../lib/protocol";

// TeamViewer-style console: a signed-in user reaches any online machine by its
// permanent 8-digit ID + the current password shown on that machine's agent.
export function ConnectByCodePage() {
  const navigate = useNavigate();
  // The Devices page links here with ?id=<code> to pre-fill the machine.
  const [params] = useSearchParams();
  const [code, setCode] = useState((params.get("id") ?? "").replace(/\D/g, "").slice(0, 8));
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"control" | "view">("control");
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);

  const digits = code.replace(/\D/g, "");
  const canConnect = digits.length === 8 && password.length > 0 && !connecting;

  function friendlyError(err: unknown): string {
    if (!(err instanceof ApiError)) return String(err);
    switch (err.code) {
      case "invalid_code_or_password":
        return "That ID or password isn't right. Check the agent screen and try again.";
      case "device_offline":
        return "That machine is offline right now.";
      case "too_many_attempts":
        return "Too many failed attempts for this ID. Wait a few minutes and retry.";
      case "unauthorized":
        return "Your session expired — please sign in again.";
      default:
        return err.code;
    }
  }

  async function connect() {
    setConnecting(true);
    setError(null);
    try {
      const res = await api<{
        sessionId: string;
        viewerTicket: string;
        hostname?: string;
        label?: string;
      }>("/v1/connect/by-code", {
        method: "POST",
        body: JSON.stringify({ code: digits, password, mode })
      });
      // v2: clear any reconnect token from a prior session BEFORE navigating — it's
      // bound to a specific device, so reusing it after a fresh manual connect to a
      // different machine would re-dial the wrong host. The Viewer mints its own.
      try {
        sessionStorage.removeItem(RECONNECT_TOKEN_KEY);
      } catch {
        /* ignore */
      }
      // v2: carry a friendly device name so a saved recording gets a descriptive filename.
      const nm = res.label || res.hostname;
      const nameParam = nm ? `&name=${encodeURIComponent(nm)}` : "";
      navigate(`/viewer/${res.sessionId}?ticket=${encodeURIComponent(res.viewerTicket)}${nameParam}`);
    } catch (err) {
      setError(friendlyError(err));
      setConnecting(false);
    }
  }

  return (
    <section className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white">Remote control</h1>
        <p className="text-white/50">
          Enter the ID &amp; password shown on the other machine's SPIDER® Viewer.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6 items-start">
        {/* Control a remote computer */}
        <div className="glass p-6 lg:col-span-3">
          <div className="flex items-center gap-2 mb-5">
            <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-600/20 text-blue-300">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </span>
            <div>
              <div className="text-white font-semibold leading-tight">Control a remote computer</div>
              <div className="text-white/40 text-xs">Enter the partner's ID and password</div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="conn-code"
                className="block text-xs uppercase tracking-wider text-white/50 mb-2 font-semibold"
              >
                Partner ID
              </label>
              <input
                id="conn-code"
                inputMode="numeric"
                autoFocus
                placeholder="0000 0000"
                value={digits.replace(/(\d{4})(?=\d)/g, "$1 ")}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canConnect) connect();
                }}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-2xl font-mono tracking-widest text-center focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div>
              <label
                htmlFor="conn-pw"
                className="block text-xs uppercase tracking-wider text-white/50 mb-2 font-semibold"
              >
                Password
              </label>
              <input
                id="conn-pw"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canConnect) connect();
                }}
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white text-xl font-mono tracking-widest text-center focus:border-blue-500 focus:outline-none"
              />
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setMode("control")}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                  mode === "control"
                    ? "bg-blue-600 text-white"
                    : "bg-white/10 text-white/60 hover:bg-white/20"
                }`}
              >
                Remote control
              </button>
              <button
                type="button"
                onClick={() => setMode("view")}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
                  mode === "view"
                    ? "bg-blue-600 text-white"
                    : "bg-white/10 text-white/60 hover:bg-white/20"
                }`}
              >
                View only
              </button>
            </div>

            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-300 text-sm">
                {error}
              </div>
            )}

            <button
              type="button"
              disabled={!canConnect}
              onClick={connect}
              className={`w-full py-3 rounded-lg font-semibold transition flex items-center justify-center gap-2 ${
                canConnect
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-white/10 text-white/40 cursor-not-allowed"
              }`}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              {connecting ? "Connecting…" : "Connect"}
            </button>

            <p className="text-xs text-white/40 text-center pt-1">
              The password changes each time the other machine's app restarts. Ask its user for the
              current one, or have them set a fixed unattended password.
            </p>
          </div>
        </div>

        {/* How it works + get the app */}
        <div className="lg:col-span-2 space-y-4">
          <div className="glass p-6">
            <div className="text-white font-semibold mb-3">How it works</div>
            <ol className="space-y-3 text-sm text-white/70">
              <li className="flex gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/10 text-white/70 text-xs font-semibold shrink-0">
                  1
                </span>
                Install the SPIDER Viewer app on the machine you want to reach.
              </li>
              <li className="flex gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/10 text-white/70 text-xs font-semibold shrink-0">
                  2
                </span>
                It shows a permanent <span className="text-white">ID</span> and a{" "}
                <span className="text-white">password</span>.
              </li>
              <li className="flex gap-3">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/10 text-white/70 text-xs font-semibold shrink-0">
                  3
                </span>
                Enter them here to take control — no ownership setup needed.
              </li>
            </ol>
          </div>

          <a
            href={APP_DOWNLOAD_URL}
            download={APP_DOWNLOAD_NAME}
            className="glass px-5 py-4 flex items-center gap-3 hover:bg-white/10 transition group"
          >
            <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-600/20 text-blue-300 group-hover:bg-blue-600/30">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v12m0 0l-4-4m4 4l4-4M4 20h16" />
              </svg>
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold text-white">Download SPIDER Viewer</span>
              <span className="block text-xs text-white/50">{APP_PLATFORM_LABEL}</span>
            </span>
            <svg className="w-5 h-5 text-white/30 group-hover:text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}
